import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { todayISO } from "@/lib/pgt";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function pad(n) { return String(n).padStart(2, "0"); }
function iso(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }

export default function FollowupCalendar({ followups, onComplete }) {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selected, setSelected] = useState(null);

  const today = todayISO();

  // group by due date
  const byDate = {};
  for (const f of followups) {
    (byDate[f.due_date] = byDate[f.due_date] || []).push(f);
  }

  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y); setSelected(null);
  };

  const overdue = followups.filter((f) => f.due_date < today);
  const selectedItems = selected ? (byDate[selected] || []) : [];

  return (
    <div>
      {overdue.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700" data-testid="calendar-overdue-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span><strong>{overdue.length}</strong> overdue follow-up{overdue.length > 1 ? "s" : ""} across all dates. Overdue days are marked in red below.</span>
        </div>
      )}

      <div className="rounded-xl border border-stone-200 bg-white p-3 sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-stone-900" data-testid="calendar-title">{MONTHS[month]} {year}</h2>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => shift(-1)} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth()); setSelected(null); }} data-testid="calendar-today">Today</Button>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => shift(1)} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="overflow-x-auto no-scrollbar">
          <div className="min-w-[640px]">
            <div className="grid grid-cols-7 gap-1.5 mb-1.5">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-center text-[11px] font-semibold uppercase tracking-wide text-stone-400">{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {cells.map((d, i) => {
                if (!d) return <div key={i} className="min-h-[76px] rounded-lg bg-stone-50/40" />;
                const dateStr = iso(year, month, d);
                const items = byDate[dateStr] || [];
                const isToday = dateStr === today;
                const isOverdue = dateStr < today && items.length > 0;
                const isSelected = selected === dateStr;
                return (
                  <button
                    key={i}
                    onClick={() => setSelected(isSelected ? null : dateStr)}
                    data-testid={`calendar-day-${dateStr}`}
                    className={`min-h-[76px] rounded-lg border p-1.5 text-left align-top transition-colors ${
                      isSelected ? "border-forest ring-1 ring-forest" : "border-stone-200"
                    } ${isOverdue ? "bg-red-50" : isToday ? "bg-forest-light" : "bg-white hover:bg-stone-50"}`}
                  >
                    <div className={`text-xs font-semibold ${isToday ? "text-forest" : isOverdue ? "text-red-600" : "text-stone-500"}`}>
                      {d}{isToday && <span className="ml-1 text-[9px] font-bold uppercase">today</span>}
                    </div>
                    <div className="mt-1 space-y-1">
                      {items.slice(0, 2).map((f) => (
                        <div
                          key={f.id}
                          className={`truncate rounded px-1 py-0.5 text-[10px] font-medium ${
                            isOverdue ? "bg-red-100 text-red-700" : "bg-forest/10 text-forest"
                          }`}
                        >
                          {f.customer?.name || "Follow-up"}
                        </div>
                      ))}
                      {items.length > 2 && <div className="text-[10px] text-stone-400">+{items.length - 2} more</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Selected day detail */}
      {selected && (
        <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4" data-testid="calendar-day-detail">
          <h3 className="mb-3 font-heading font-semibold text-stone-900">{selectedItems.length} follow-up{selectedItems.length !== 1 ? "s" : ""} on {selected}</h3>
          {selectedItems.length === 0 ? (
            <p className="text-sm text-stone-400">No follow-ups scheduled for this day.</p>
          ) : (
            <div className="space-y-2">
              {selectedItems.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 p-3">
                  <button className="min-w-0 text-left" onClick={() => navigate(`/customers/${f.customer.id}`)} data-testid={`calendar-followup-${f.id}`}>
                    <div className="font-medium text-stone-800 truncate">{f.customer?.name}</div>
                    <div className="text-xs text-stone-500 truncate">{f.reason || "Follow up"}{f.customer?.company ? ` · ${f.customer.company}` : ""}</div>
                  </button>
                  <Button size="sm" variant="outline" className="h-8 shrink-0 text-xs" onClick={() => onComplete(f.id)} data-testid={`calendar-complete-${f.id}`}>
                    <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Done
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
